import ArrowLeft from "@/assets/icons/ArrowLeft";
import ArrowRight from "@/assets/icons/ArrowRight";
import Screen from "@/components/layout/Screen";
import AppButton from "@/components/shared/AppButton";
import PageTitle from "@/components/shared/PageTitle/PageTitle";
import PasswordInput from "@/components/shared/PasswordInput";
import { FormControl, FormControlError, FormControlErrorText, FormControlLabel, FormControlLabelText } from "@/components/ui/form-control";
import { HStack } from "@/components/ui/hstack";
import { Input, InputField } from "@/components/ui/input";
import { Text } from "@/components/ui/text";
import { VStack } from "@/components/ui/vstack";
import { useAuth } from "@/contexts/AuthContext";
import { useAuthNavigation } from "@/hooks/useAuthNavigation";
import { formatPhoneNumber, getPhoneDigits } from "@/utils/formatPhoneNumber";
import { removeFromStorage } from "@/utils/secureStore";
import { newPasswordSchema } from "@/utils/validationSchemas";
import { zodResolver } from "@hookform/resolvers/zod";
import { T, useT } from "@transifex/react";
import { useFocusEffect, useLocalSearchParams, useRouter } from "expo-router";
import { useCallback } from "react";
import { type Control, Controller, Form, type FormState, type UseFormClearErrors, useForm } from "react-hook-form";
import { Platform } from "react-native";
import type { z } from "zod";

type NewPasswordSchemaType = z.infer<typeof newPasswordSchema>;

type UserParams = { email: string; first?: string; last?: string; phone?: string; session: string };

interface NewPasswordFormProps {
  clearErrors: UseFormClearErrors<NewPasswordSchemaType>;
  control: Control<NewPasswordSchemaType>;
  errors: FormState<NewPasswordSchemaType>["errors"];
  handleSubmit: () => Promise<void>;
  isLoading: boolean;
  userParams: UserParams;
}

const NewPasswordForm = ({ clearErrors, control, errors, handleSubmit, isLoading, userParams }: NewPasswordFormProps) => {
  const t = useT();
  return (
    <Form control={control} onSubmit={handleSubmit}>
      <VStack className="gap-[20px]">
        <FormControl isDisabled={isLoading} isInvalid={!!errors.first} key="new-password-first" className={`gap-[10px] ${!!userParams.first && "hidden"}`}>
          <FormControlLabel>
            <FormControlLabelText className="font-[600] leading-[20px] text-[14px] text-typography-900 align-middle">
              <T _str="First Name" />
            </FormControlLabelText>
          </FormControlLabel>
          <Controller
            control={control}
            name="first"
            render={({ field: { onChange, onBlur, value } }) => (
              <Input
                className={`bg-background-100 h-[52px] pl-[8px] pr-[16px] py-[8px] rounded-[6px] ${errors.first ? "bg-error-50 border-error-400" : "bg-background-100 border-secondary-700"}`}
              >
                <InputField
                  accessibilityLabel={t("First Name")}
                  autoCapitalize="words"
                  autoComplete="given-name"
                  className="text-typography-900 text-[16px] font-[400] leading-[22px] align-middle placeholder-text-typography-900 placeholder-opacity-50"
                  onBlur={onBlur}
                  onChangeText={onChange}
                  onFocus={() => clearErrors("first")}
                  placeholder={t("Enter Your First Name")}
                  returnKeyType="next"
                  value={value}
                />
              </Input>
            )}
          />
          <FormControlError>
            <FormControlErrorText className="text-error-100 text-[14px] leading-[20px]">{errors.first?.message}</FormControlErrorText>
          </FormControlError>
        </FormControl>
        <FormControl isDisabled={isLoading} isInvalid={!!errors.last} key="new-password-last" className={`gap-[10px] ${!!userParams.last && "hidden"}`}>
          <FormControlLabel>
            <FormControlLabelText className="font-[600] leading-[20px] text-[14px] text-typography-900 align-middle">
              <T _str="Last Name" />
            </FormControlLabelText>
          </FormControlLabel>
          <Controller
            control={control}
            name="last"
            render={({ field: { onChange, onBlur, value } }) => (
              <Input
                className={`bg-background-100 h-[52px] pl-[8px] pr-[16px] py-[8px] rounded-[6px] ${errors.last ? "bg-error-50 border-error-400" : "bg-background-100 border-secondary-700"}`}
              >
                <InputField
                  accessibilityLabel={t("Last Name")}
                  autoCapitalize="words"
                  autoComplete="family-name"
                  className="text-typography-900 text-[16px] font-[400] leading-[22px] align-middle placeholder-text-typography-900 placeholder-opacity-50"
                  placeholder={t("Enter Your Last Name")}
                  onBlur={onBlur}
                  onChangeText={onChange}
                  onFocus={() => errors.last && clearErrors("last")}
                  returnKeyType="next"
                  value={value}
                />
              </Input>
            )}
          />
          <FormControlError>
            <FormControlErrorText className="text-error-100 text-[14px] leading-[20px]">{errors.last?.message}</FormControlErrorText>
          </FormControlError>
        </FormControl>
        <FormControl isDisabled={isLoading} isInvalid={!!errors.phone} key="new-password-phone" className={`gap-[10px] ${!!userParams.phone && "hidden"}`}>
          <FormControlLabel>
            <FormControlLabelText className="font-[600] leading-[20px] text-[14px] text-typography-900 align-middle">
              <T _str="Phone number" />
            </FormControlLabelText>
          </FormControlLabel>
          <Controller
            name="phone"
            control={control}
            render={({ field: { onChange, onBlur, value } }) => (
              <Input
                className={`bg-background-100 h-[52px] pl-[8px] pr-[16px] py-[8px] rounded-[6px] ${errors.phone ? "bg-error-50 border-error-400" : "bg-background-100 border-secondary-700"}`}
              >
                <InputField
                  accessibilityLabel={t("Phone Number")}
                  autoComplete="tel"
                  placeholder={t("+1 (xxx) xxx-xxxx")}
                  keyboardType="phone-pad"
                  onBlur={onBlur}
                  onChangeText={(text: string) => onChange(getPhoneDigits(text))}
                  onFocus={() => clearErrors("phone")}
                  returnKeyType="next"
                  value={typeof value === "string" ? formatPhoneNumber(value) : ""}
                />
              </Input>
            )}
          />
          <FormControlError>
            <FormControlErrorText className="text-error-100 text-[14px] leading-[20px]">{errors.phone?.message}</FormControlErrorText>
          </FormControlError>
        </FormControl>
        <FormControl isDisabled={isLoading} isInvalid={!!errors.newPassword} key="new-password-password" className="gap-[10px]">
          <FormControlLabel>
            <FormControlLabelText className="font-[600] leading-[20px] text-[14px] text-typography-900 align-middle">
              <T _str="New password" />
            </FormControlLabelText>
          </FormControlLabel>
          <Controller
            control={control}
            name="newPassword"
            render={({ field: { onChange, onBlur, value } }) => (
              <PasswordInput
                accessibilityLabel={t("New password")}
                autoComplete="password-new"
                error={errors.newPassword?.message}
                onBlur={onBlur}
                onChangeText={onChange}
                onFocus={() => clearErrors("newPassword")}
                value={value}
              />
            )}
          />
          <FormControlError>
            <FormControlErrorText className="text-error-100 text-[14px] leading-[20px]">{errors.newPassword?.message}</FormControlErrorText>
          </FormControlError>
        </FormControl>
        <FormControl isDisabled={isLoading} isInvalid={!!errors.newPasswordConfirmation} key="new-password-password-confirmation" className="gap-[10px]">
          <FormControlLabel>
            <FormControlLabelText className="font-[600] leading-[20px] text-[14px] text-typography-900 align-middle">
              <T _str="Confirm new password" />
            </FormControlLabelText>
          </FormControlLabel>
          <Controller
            control={control}
            name="newPasswordConfirmation"
            render={({ field: { onChange, onBlur, value } }) => (
              <PasswordInput
                accessibilityLabel={t("Confirm new password")}
                autoComplete="password-new"
                error={errors.newPasswordConfirmation?.message}
                onBlur={onBlur}
                onChangeText={onChange}
                onFocus={() => clearErrors("newPasswordConfirmation")}
                value={value}
              />
            )}
          />
          <FormControlError>
            <FormControlErrorText className="text-error-100 text-[14px] leading-[20px]">{errors.newPasswordConfirmation?.message}</FormControlErrorText>
          </FormControlError>
        </FormControl>
      </VStack>
      {Platform.OS === "web" && <button className="hidden" type="submit" />}
    </Form>
  );
};

export default function NewPassword() {
  const userParams = useLocalSearchParams<UserParams>();
  const { isLoading, setNewPassword } = useAuth();
  const router = useRouter();
  const handleAuthNavigation = useAuthNavigation();
  const t = useT();

  const {
    clearErrors,
    control,
    reset,
    handleSubmit,
    formState: { errors },
  } = useForm<NewPasswordSchemaType>({
    resolver: zodResolver(newPasswordSchema),
    defaultValues: {
      first: userParams.first ?? "",
      last: userParams.last ?? "",
      newPassword: "",
      newPasswordConfirmation: "",
      phone: userParams.phone ? `+${getPhoneDigits(userParams.phone)}` : "",
    },
  });

  useFocusEffect(
    useCallback(() => {
      reset();
      return reset;
    }, [reset]),
  );

  const goBack = useCallback(() => {
    if (router.canGoBack()) {
      router.back();
    } else {
      router.replace("/welcome");
    }
  }, [router]);

  const getPhoneDigitsWithPlus = useCallback((phone: string) => {
    return phone ? `+${getPhoneDigits(phone)}` : null;
  }, []);

  const onSubmit = useCallback(
    async (data: NewPasswordSchemaType) => {
      try {
        await setNewPassword(
          userParams.email,
          data.newPassword,
          userParams.first ?? data.first,
          userParams.last ?? data.last,
          getPhoneDigitsWithPlus(data.phone),
          userParams.phone,
          userParams.session,
        );
        await removeFromStorage("guestRecentPlates");
        await removeFromStorage("contact");
        handleAuthNavigation();
      } catch (err) {
        console.error("New password error:", err);
        if (err instanceof Error && err.name === "UserNotConfirmedException") {
          router.replace("/auth/sign-up?verify=true");
        }
      }
    },
    [getPhoneDigitsWithPlus, handleAuthNavigation, router, setNewPassword, userParams],
  );

  return (
    <Screen>
      <VStack className="gap-[32px]">
        <PageTitle title={t("Welcome")} />

        <Text className="text-[14px] font-[400] leading-[20px] text-center text-typography-900">
          {!userParams.first || !userParams.last || !userParams.phone ? (
            <T _str="To continue, please update your account information and set a new password" />
          ) : (
            <T _str="Your account requires a new password to be set." />
          )}
        </Text>

        <NewPasswordForm
          clearErrors={clearErrors}
          control={control}
          errors={errors}
          handleSubmit={handleSubmit(onSubmit)}
          isLoading={isLoading}
          userParams={userParams}
        />

        <HStack className="gap-[12px]">
          <AppButton className="flex-1" disabled={isLoading} icon={ArrowLeft} onPress={goBack} title={t("Back")} variant="outline" />
          <AppButton className="flex-1" icon={ArrowRight} iconPosition="after" loading={isLoading} onPress={handleSubmit(onSubmit)} title={t("Submit")} />
        </HStack>
      </VStack>
    </Screen>
  );
}
